import { VINTAGE_COLORS, VINTAGE_PLOT_LIMIT } from '../constants';

// ---------------------------------------------------------------------------
// Safra (vintage) — geometria do gráfico e derivações de leitura.
//
// Mora fora do componente porque é aritmética pura: escala do eixo, posição dos
// pontos, escolha das safras plotadas. Componente cuida de pintar.
// ---------------------------------------------------------------------------

// Área de plotagem, em unidades do viewBox (720 × 280).
export const PLOT = {
   width: 720,
   height: 280,
   x0: 56,
   x1: 700,
   yBase: 232,
   yTop: 24,
   maxMonth: 12,
};

// Passos "redondos" — o eixo Y só assume valores que uma pessoa lê sem
// traduzir. 4 divisões, então YMAX = passo × 4.
const STEPS = [0.5, 1, 2, 2.5, 5, 10];
const DIVISIONS = 4;

// Escolhe o menor passo que acomoda o pico com 15% de folga no topo, para a
// curva mais alta não encostar na borda.
export function pickStep(peak) {
   const needed = peak * 1.15;
   return STEPS.find((step) => step * DIVISIONS >= needed) ?? 25;
}

export function buildScale(safras) {
   const values = safras.flatMap((safra) =>
      (safra.pontos ?? [])
         .map((point) => point?.pct)
         .filter((pct) => pct !== null && pct !== undefined),
   );

   const peak = values.length ? Math.max(...values) : 0;
   const step = pickStep(peak);
   const max = step * DIVISIONS;

   return {
      step,
      max,
      // Os rótulos caem em múltiplos EXATOS do passo — nada de arredondar
      // rótulo de tick, que é como um eixo passa a mentir sobre a grade.
      ticks: Array.from({ length: DIVISIONS + 1 }, (_, index) => step * index),
   };
}

export function xOf(month) {
   return PLOT.x0 + (month / PLOT.maxMonth) * (PLOT.x1 - PLOT.x0);
}

export function yOf(pct, max) {
   if (!max) return PLOT.yBase;
   const ratio = Math.max(0, Math.min(1, pct / max));
   return PLOT.yBase - ratio * (PLOT.yBase - PLOT.yTop);
}

// Percentuais para posicionar rótulos em HTML sobre um SVG que escala junto com
// o card (o viewBox é fixo, a caixa não).
export function toPercentX(x) {
   return (x / PLOT.width) * 100;
}

export function toPercentY(y) {
   return (y / PLOT.height) * 100;
}

// ⚠️ Ponto `null` NÃO é plotado, não é interpolado e não é zero: significa "M+n
// ainda não ocorreu". Uma safra recente termina antes — é o comportamento
// correto, não bug de renderização. Zero leria como "safra impecável", que é o
// oposto da verdade.
export function plottablePoints(safra, max) {
   return (safra.pontos ?? [])
      .filter((point) => point?.pct !== null && point?.pct !== undefined)
      .map((point) => ({
         ...point,
         x: xOf(point.m),
         y: yOf(point.pct, max),
      }));
}

// Quantos pontos de fato plotáveis a safra tem.
export function plottablePointCount(safra) {
   return (safra?.pontos ?? []).filter(
      (point) => point?.pct !== null && point?.pct !== undefined,
   ).length;
}

function orderBySafra(safras) {
   return [...safras].sort((a, b) =>
      String(a.safra).localeCompare(String(b.safra)),
   );
}

// Quais safras o gráfico sugere quando o operador ainda não escolheu.
//
// ⚠️ Isto já foi "as N mais recentes", e estava ERRADO. Safra precisa de IDADE:
// as mais recentes são justamente as que ainda não têm ponto nenhum. Em
// 08/2026, com régua de 90 dias, as 6 safras mais novas rendiam dois círculos
// soltos e nenhuma linha — uma linha precisa de 2 pontos. O gráfico nascia
// vazio e a métrica parecia quebrada.
//
// Agora o default são as mais recentes **que tenham ao menos 2 pontos**: toda
// linha sugerida é uma linha de verdade. As demais continuam na tabela abaixo,
// e o operador as traz para o gráfico clicando na linha.
export function defaultPlottedSafras(safras) {
   return orderBySafra(safras)
      .filter((safra) => plottablePointCount(safra) >= 2)
      .slice(-VINTAGE_PLOT_LIMIT)
      .map((safra) => safra.safra);
}

// Monta as safras selecionadas com cor e marcação de origem.
//
// A cor sai da posição CRONOLÓGICA dentro da seleção — mais antiga clara, mais
// recente escura —, então comparar 2024 com 2026 mantém a leitura "escuro =
// recente" que a rampa ordinal promete.
export function buildPlottedSafras(safras, selectedKeys) {
   const selected = orderBySafra(
      safras.filter((safra) => selectedKeys.includes(safra.safra)),
   );

   // Acima do tamanho da rampa as cores se repetiriam — melhor cortar do que
   // ter duas safras da mesma cor no mesmo gráfico.
   const visible = selected.slice(0, VINTAGE_COLORS.length);

   return visible.map((safra, index) => ({
      ...safra,
      color: VINTAGE_COLORS[VINTAGE_COLORS.length - visible.length + index],
      isHistorical: safra.origem === 'carga_historica',
   }));
}

// ─── Heatmap ──────────────────────────────────────────────────────────────

// Faixas de severidade do atraso. É escala de STATUS (bom → crítico), não
// identidade de série: verde/amarelo/laranja/vermelho têm significado fixo.
const HEAT_BANDS = [
   { limit: 2, rgb: '46, 125, 50', ink: '#1b5e20' },
   { limit: 4.5, rgb: '255, 193, 7', ink: '#7d5a00' },
   { limit: 7, rgb: '230, 81, 0', ink: '#bf360c' },
   { limit: Infinity, rgb: '211, 47, 47', ink: '#b71c1c' },
];

export function heatStyle(pct) {
   if (pct === null || pct === undefined) return null;
   const band = HEAT_BANDS.find((entry) => pct < entry.limit);
   // A intensidade acompanha o valor dentro da faixa, com teto em 9% — acima
   // disso a saturação já não acrescenta leitura.
   const alpha = 0.14 + Math.min(pct / 9, 1) * 0.38;
   return {
      background: `rgba(${band.rgb}, ${alpha})`,
      borderColor: `rgba(${band.rgb}, 0.35)`,
      color: band.ink,
   };
}
